import type { ProposalListItem } from '@/lib/proposals/core/types';
import { isOnboardingProposal } from '@/lib/proposals/utils/onboardingConfig';

import { isCrmOnlyProposal } from './isCrmOnlyProposal';

export type PriorityTier = 'high' | 'medium' | 'crm_updates';

export interface PriorityGroup {
  tier: PriorityTier;
  label: string;
  actions: ProposalListItem[];
}

/**
 * Classify a single proposal into its priority tier.
 * Single source of truth for the tier rules shared by all list surfaces.
 */
export function classifyPriorityTier(item: ProposalListItem): PriorityTier {
  if (isCrmOnlyProposal(item)) {
    return 'crm_updates';
  }
  const pri = item.priority ?? 4;
  return pri <= 2 ? 'high' : 'medium';
}

/**
 * Recency key for intra-group ordering. Open proposals have no resolved_at,
 * so they order by creation; history views (Done / Dismissed / Archived)
 * order by when the proposal was resolved — "recency" from the user's point
 * of view in each context.
 */
function recencyKey(p: ProposalListItem): number {
  return new Date(p.resolved_at ?? p.created_at).getTime();
}

/**
 * Group proposals into the priority tiers the AI Actions table renders as
 * section bands. Single source of truth for the classification, shared by
 * the Open list and the history views (Done / Dismissed / Archived) so every
 * view shows identical groupings.
 *
 * Classification:
 *  - CRM-only proposals (every action tag is a CRM operation) → "CRM updates",
 *    regardless of priority
 *  - otherwise priority <= 2 → "High priority", else "Medium priority"
 *
 * Within each group, proposals sort newest-first by `resolved_at ?? created_at`
 * (product decision 2026-09-01): the band is the only ranking the user can
 * see, so the backend's hidden intra-tier score ordering — and the raw
 * priority sub-order within the "High" band — read as arbitrary and are
 * deliberately discarded here. Empty groups are omitted.
 */
export function groupProposalsByTier(
  items: ProposalListItem[]
): PriorityGroup[] {
  const high: ProposalListItem[] = [];
  const medium: ProposalListItem[] = [];
  const crmUpdates: ProposalListItem[] = [];

  for (const p of items) {
    const tier = classifyPriorityTier(p);
    if (tier === 'crm_updates') {
      crmUpdates.push(p);
    } else if (tier === 'high') {
      high.push(p);
    } else {
      medium.push(p);
    }
  }

  // Precompute each proposal's recency key once — avoids allocating a new
  // Date on every comparator invocation across the O(n log n) sorts below.
  const recencyKeys = new Map<number, number>(
    items.map(p => [p.id, recencyKey(p)])
  );
  const byRecency = (a: ProposalListItem, b: ProposalListItem) =>
    (recencyKeys.get(b.id) ?? 0) - (recencyKeys.get(a.id) ?? 0);
  high.sort(byRecency);
  medium.sort(byRecency);
  crmUpdates.sort(byRecency);

  const groups: PriorityGroup[] = [];
  if (high.length > 0) {
    groups.push({ tier: 'high', label: 'High priority', actions: high });
  }
  if (medium.length > 0) {
    groups.push({ tier: 'medium', label: 'Medium priority', actions: medium });
  }
  if (crmUpdates.length > 0) {
    groups.push({
      tier: 'crm_updates',
      label: 'CRM updates',
      actions: crmUpdates,
    });
  }
  return groups;
}

/**
 * Flatten proposals to the exact display order the AI Actions list renders —
 * priority bands via {@link groupProposalsByTier}, newest-first within each —
 * excluding onboarding proposals (the list renders those in a separate pinned
 * section outside the bands).
 *
 * This is the order "the next action" navigation is defined against:
 * ActionDetailPage feeds it into `utils/getNextActionId` when approving /
 * dismissing on the detail route. ProposalListPanel's own
 * `flattenedActionIds` memo flattens its already-computed `priorityGroups`
 * the same way (plus its pinned-onboarding prefix) — if the panel's flatten
 * rule ever changes, this helper must change with it.
 */
export function flattenProposalsToDisplayOrder(
  proposals: readonly ProposalListItem[]
): number[] {
  return groupProposalsByTier(
    proposals.filter(p => !isOnboardingProposal(p))
  ).flatMap(group => group.actions.map(action => action.id));
}
